import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { insidePages } from '../data/content';
export function InsideCarousel() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const update = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const slide = el.firstElementChild as HTMLElement | null;
    const step = slide ? slide.offsetWidth + 16 : el.clientWidth;
    setActive(Math.round(el.scrollLeft / step));
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [update]);
  const scrollTo = (index: number) => {
    const el = trackRef.current;
    const target = el?.children[index] as HTMLElement | undefined;
    if (!el || !target) return;
    el.scrollTo({
      left: target.offsetLeft - el.offsetLeft,
      behavior: 'smooth'
    });
  };
  const go = (dir: 1 | -1) => scrollTo(Math.min(insidePages.length - 1, Math.max(0, active + dir)));
  const arrowClass = 'flex h-11 w-11 items-center justify-center rounded-full border-2 border-ink bg-cream text-ink shadow-[3px_3px_0_0_#141210] transition-[background-color,opacity,transform] duration-150 ease-out hover:bg-kraft-light active:translate-x-[1px] active:translate-y-[1px] disabled:pointer-events-none disabled:opacity-30 focus:outline-none focus-visible:ring-4 focus-visible:ring-rust/40';
  return <section aria-labelledby="inside-title" aria-roledescription="carousel" className="border-b-2 border-ink bg-paper" data-id="element-88" data-mp="3kh2d1hqbe5fz">
      <div className="mx-auto max-w-6xl px-5 py-12 lg:py-20" data-id="element-89" data-mp="3ojpb40dkwmlm">
        <div className="flex items-end justify-between gap-4" data-id="element-90" data-mp="3tgxrbvf62gk8">
          <h2 id="inside-title" className="font-display text-[2rem] font-black leading-tight tracking-tight sm:text-5xl" data-id="element-91" data-mp="3tfe8bx24lrer">
            A look inside the journal
          </h2>
          <div className="flex shrink-0 gap-2" data-id="element-92" data-mp="1pf7cngkmpkx6">
            <button type="button" onClick={() => go(-1)} disabled={!canPrev} aria-label="Previous page" className={arrowClass} data-id="element-93" data-mp="234r9k5rj3nyk">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-94" data-mp="1yd9hk77hgxpq" />
            </button>
            <button type="button" onClick={() => go(1)} disabled={!canNext} aria-label="Next page" className={arrowClass} data-id="element-95" data-mp="2d2fflrul4hw1">
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-96" data-mp="283m05tvqmgt8" />
            </button>
          </div>
        </div>
        <ul ref={trackRef} onScroll={update} className="-mx-5 mt-6 flex snap-x snap-mandatory scroll-px-5 gap-4 overflow-x-auto px-5 pb-4 [scrollbar-width:none] sm:mt-8 [&::-webkit-scrollbar]:hidden" data-id="element-97" data-mp="2m1spumqj9aht">
          {insidePages.map((page, i) => <li key={page.src} aria-label={`${i + 1} of ${insidePages.length}`} className="w-[80%] shrink-0 snap-start sm:w-[calc((100%-1rem)/2)] lg:w-[calc((100%-2rem)/3)]" data-id="element-98" data-mp="mslxagvca4j3" data-mpi={i}>
              <div className="overflow-hidden rounded-xl border-2 border-ink bg-white shadow-[4px_4px_0_0_#141210]" data-id="element-99" data-mp="2plswilm0u0z6">
                <img src={page.src} alt={page.alt} className="aspect-[5/7] w-full object-cover object-top" loading="lazy" data-id="element-100" data-mp="2tmwat3of7oya" />
              </div>
            </li>)}
        </ul>
        <div className="mt-4 flex justify-center gap-2" data-id="element-101" data-mp="2kmq2rxbhrh6i">
          {insidePages.map((page, i) => <button key={page.src} type="button" onClick={() => scrollTo(i)} aria-label={`Go to page ${i + 1}`} aria-current={active === i} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out ${active === i ? 'w-6 bg-ink' : 'w-2.5 bg-kraft-dark/40 hover:bg-kraft-dark'}`} data-id="element-102" data-mp="26myjbh27h8l4" data-mpi={i} />)}
        </div>
      </div>
    </section>;
}